import { forwardRef } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';

type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> & {
  className?: string;
  /** Renders the error treatment. Pair with aria-invalid on the caller. */
  invalid?: boolean;
  /**
   * Icon or control at the inline START (the right edge in Arabic). Size icons at
   * 1.25rem (`h-5 w-5`). Decorative content lets clicks through to the field;
   * buttons and links inside stay clickable.
   */
  startAdornment?: ReactNode;
  /** Same, at the inline END (a password toggle, a unit, a clear button). */
  endAdornment?: ReactNode;
  /** Classes for the wrapper rendered when an adornment is present. */
  wrapperClassName?: string;
};

/**
 * The one text field in the design system. See Apps/web/THEME.md.
 *
 * White surface, green hairline, and the single brand focus ring — the same
 * ring Button uses, so every focusable thing on the site looks alike.
 *
 * Without adornments the DOM is a bare <input>, exactly as before. With either one,
 * the input sits in a `relative` wrapper and gets logical padding (`ps-11` / `pe-11`)
 * so the text never runs under the icon, in either direction.
 */
const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className = '', invalid = false, startAdornment, endAdornment, wrapperClassName = '', ...rest },
  ref
) {
  const base =
    'w-full rounded-xl border bg-white px-4 py-3 sm:py-2.5 text-base sm:text-sm text-[var(--pv-color-text)] ' +
    'placeholder:text-[var(--pv-color-text-subtle)] outline-none transition-colors duration-200 min-h-[44px] ' +
    'disabled:cursor-not-allowed disabled:bg-[var(--pv-color-surface-soft)] disabled:text-[var(--pv-color-text-subtle)] ' +
    'dark:bg-[#08372F] dark:text-[#E6F3EF] dark:placeholder:text-[#6A8480]';

  const state = invalid
    ? 'border-[#DF2F2B] focus:border-[#DF2F2B] focus:ring-2 focus:ring-[#DF2F2B]/20'
    : 'border-[var(--pv-border-soft)] hover:border-[var(--pv-color-accent-soft)] focus:border-[#004D40] focus:ring-2 focus:ring-[#004D40]/20 ' +
      'dark:border-white/15 dark:hover:border-white/25 dark:focus:border-[#3FBBA2] dark:focus:ring-[#3FBBA2]/25';

  if (!startAdornment && !endAdornment) {
    return <input ref={ref} {...rest} className={`${base} ${state} ${className}`} />;
  }

  // The slot ignores the pointer so a click on an icon focuses the field, but any
  // button or link placed in it stays clickable.
  const slot =
    'pointer-events-none absolute inset-y-0 flex items-center text-[var(--pv-color-text-muted)] ' +
    '[&_a]:pointer-events-auto [&_button]:pointer-events-auto';
  const padding = `${startAdornment ? 'ps-11' : ''} ${endAdornment ? 'pe-11' : ''}`;

  return (
    <div className={`relative ${wrapperClassName}`}>
      {startAdornment && <span className={`${slot} start-3`}>{startAdornment}</span>}
      <input ref={ref} {...rest} className={`${base} ${state} ${padding} ${className}`} />
      {endAdornment && <span className={`${slot} end-3`}>{endAdornment}</span>}
    </div>
  );
});

export default Input;
